import { AuthProvider } from "@refinedev/core";
import { axiosInstance } from "./customDataProvider";
export { axiosInstance };
import { APP_CONFIG } from "./constants";
import { getUserAvatarUrl } from "./utils/imageHelper";
import { chLogger } from "./components/DebugOverlay";
import { isTransientNetworkError } from "./utils/clientEnvironment";
import { hasNativeAuthBridge, requestNativeAccessToken } from "./mobileAuthBridge";
import { getFirstTouch } from "./utils/firstTouch";

const API_URL = APP_CONFIG.API_BASE;
export const TOKEN_KEY = APP_CONFIG.STORAGE_KEYS.TOKEN;

const decodeJwt = (token: string) => {
  try {
    const base64Url = token.split(".")[1];
    if (!base64Url) return null;
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const pad = base64.length % 4;
    const paddedBase64 = pad ? base64 + "=".repeat(4 - pad) : base64;
    const jsonPayload = decodeURIComponent(
      atob(paddedBase64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    console.error("Failed to decode JWT:", e);
    return null;
  }
};

const refreshBrowserAccessToken = async (): Promise<boolean> => {
  try {
    const response = await fetch(`${API_URL}/auth/refresh-token`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: "null"
    });
    if (!response.ok) return false;
    const data = await response.json();
    if (!data?.token) return false;
    localStorage.setItem(TOKEN_KEY, data.token);
    window.dispatchEvent(new CustomEvent("auth-state-changed"));
    return true;
  } catch { return false; }
};

export const authProvider: AuthProvider = {
  login: async ({ email, password, providerName, idToken }) => {
    try {
      let response;

      if (providerName === "firebase") {
        const firstTouch = getFirstTouch();
        response = await axiosInstance.post(`${API_URL}/auth/firebase-signin`, {
          idToken,
          acquisitionSource: firstTouch?.source,
          acquisitionPlatform: firstTouch?.platform,
          analyticsSessionId: firstTouch?.sessionId,
          acquisitionLandingPage: firstTouch?.landingPage
        });
      } else {
        response = await axiosInstance.post(`${API_URL}/auth/login`, {
          userNameOrEmail: email,
          password,
        });
      }

      const data = response.data;
      const requiresWebUsageType = Boolean(data.requiresUsageType) && !hasNativeAuthBridge();

      localStorage.setItem(TOKEN_KEY, data.token);
      localStorage.removeItem(APP_CONFIG.STORAGE_KEYS.REFRESH);
      window.dispatchEvent(new CustomEvent("auth-state-changed"));

      if (typeof window !== "undefined" && data.token && data.refreshToken) {
        (window as any).flutter_inappwebview?.callHandler?.("TOKEN_UPDATED", {
          accessToken: data.token,
          refreshToken: data.refreshToken
        });
      }

      return {
        success: true,
        redirectTo: requiresWebUsageType ? "/onboarding/account-type" : "/dashboard",
      };
    } catch (error: any) {
      const message =
        error.response?.data?.message ||
        (typeof error.response?.data === "string" ? error.response.data : null) ||
        "Giriş başarısız. Lütfen bilgilerinizi kontrol edin.";
      return Promise.reject(new Error(message));
    }
  },

  logout: async () => {
    try {
      await axiosInstance.post(`${API_URL}/Auth/logout-session`, null, { withCredentials: true });
    } catch (error) {
      console.error("Logout API call failed:", error);
    }

    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(APP_CONFIG.STORAGE_KEYS.REFRESH);
    window.dispatchEvent(new CustomEvent("auth-state-changed"));

    if (typeof window !== "undefined") {
      (window as any).flutter_inappwebview?.callHandler?.("TOKEN_CLEARED");
    }

    try {
      const [{ signOut }, { auth }] = await Promise.all([
        import("firebase/auth"),
        import("./firebaseAuth"),
      ]);
      await signOut(auth);
    } catch (error) {
      console.error("Firebase signOut failed:", error);
    }

    return {
      success: true,
      redirectTo: "/login",
    };
  },

  register: async ({ email, password, username, name, surname, usageType }) => {
    try {
      const formData = new FormData();
      formData.append("Email", email);
      formData.append("Password", password);
      formData.append("UserName", username || email.split("@")[0]);
      formData.append("Name", name || "");
      formData.append("Surname", surname || "");
      if (usageType !== undefined && usageType !== null && usageType !== "") {
        formData.append("UsageType", String(usageType));
      }
      const firstTouch = getFirstTouch();
      if (firstTouch) {
        formData.append("AcquisitionSource", firstTouch.source);
        formData.append("AcquisitionPlatform", firstTouch.platform);
        formData.append("AcquisitionLandingPage", firstTouch.landingPage);
        formData.append("AnalyticsSessionId", firstTouch.sessionId);
        if (firstTouch.referrer) formData.append("AcquisitionReferrer", firstTouch.referrer);
        if (firstTouch.utmMedium) formData.append("AcquisitionMedium", firstTouch.utmMedium);
        if (firstTouch.utmCampaign) formData.append("AcquisitionCampaign", firstTouch.utmCampaign);
      }

      await axiosInstance.post(`${API_URL}/auth/register`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      return {
        success: true,
      };
    } catch (error: any) {
      return {
        success: false,
        error: {
          name: "RegisterError",
          message: error.response?.data?.message || "Kayıt olurken bir hata oluştu.",
        },
      };
    }
  },

  forgotPassword: async ({ email }) => {
    try {
      await axiosInstance.post(`${API_URL}/auth/forgot-password`, { email });
      return { success: true };
    } catch (error: any) {
      return {
        success: false,
        error: {
          name: "ForgotPasswordError",
          message: error.response?.data?.message || "Şifre sıfırlama e-postası gönderilemedi.",
        },
      };
    }
  },

  updatePassword: async ({ password, confirmPassword, email, token }) => {
    try {
      if (password !== confirmPassword) {
        throw new Error("Şifreler uyuşmuyor.");
      }

      await axiosInstance.post(`${API_URL}/auth/reset-password`, {
        email,
        token,
        password,
      });

      return {
        success: true,
        redirectTo: "/login",
      };
    } catch (error: any) {
      return {
        success: false,
        error: {
          name: "UpdatePasswordError",
          message: error.message || error.response?.data?.message || "Şifre güncellenemedi.",
        },
      };
    }
  },

  check: async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    // console.log('[authProvider.check] token exists?', !!token);
    if (!token) {
      chLogger("[Auth] check: NO token found");
      return { authenticated: false };
    }

    try {
      const payload = decodeJwt(token);
      if (!payload) {
        chLogger("[Auth] check: JWT decode FAILED");
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(APP_CONFIG.STORAGE_KEYS.REFRESH);
        return { authenticated: false };
      }
      const isExpired = payload.exp && payload.exp * 1000 < Date.now();

      if (isExpired) {
        chLogger(`[Auth] check: token EXPIRED (${new Date(payload.exp * 1000).toLocaleString()})`);
        if (hasNativeAuthBridge()) {
          const renewed = await requestNativeAccessToken(true);
          if (renewed) return { authenticated: true };
        }
        if (!hasNativeAuthBridge() && await refreshBrowserAccessToken()) return { authenticated: true };
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(APP_CONFIG.STORAGE_KEYS.REFRESH);
        return { authenticated: false };
      }

      const authLabel =
        payload.sub ||
        payload.unique_name ||
        payload.name ||
        payload.email ||
        payload.userName ||
        "authenticated";
      chLogger(`[Auth] check: OK (user: ${authLabel})`);
      return { authenticated: true };
    } catch (e) {
      // console.log('[authProvider.check] → NOT authenticated (parse error)', e);
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(APP_CONFIG.STORAGE_KEYS.REFRESH);
      return { authenticated: false };
    }
  },

  getPermissions: async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return null;

    try {
      const payload = decodeJwt(token);
      if (!payload) return [];
      // ASP.NET Core Identity "role" claim'ini hem tekli hem çoklu olarak döner
      const roleClaim =
        payload["role"] ||
        payload["http://schemas.microsoft.com/ws/2008/06/identity/claims/role"];

      if (!roleClaim) return [];
      return Array.isArray(roleClaim) ? roleClaim : [roleClaim];
    } catch {
      return [];
    }
  },

  getIdentity: async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return null;

    try {
      const payload = decodeJwt(token);
      if (!payload) return null;

      const fallbackIdentity = {
        id: payload.sub || payload.id || payload["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier"],
        name: payload.username || payload.name || payload.email || payload["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name"],
        userName: payload.userName || payload.username || payload.name || payload["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name"],
        email: payload.email || payload["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress"],
        avatar: getUserAvatarUrl(payload.imageUrl || payload.avatar),
        activeFrame: payload.activeFrame || payload.ActiveFrame,
        activeCardFrame: payload.activeCardFrame || payload.ActiveCardFrame,
        activeTheme: payload.activeTheme || payload.ActiveTheme,
        activeBadge: payload.activeBadge || payload.ActiveBadge,
        activeBadges: payload.activeBadges || payload.ActiveBadges,
        roles: [],
        isOnboarded: payload.isOnboarded === "True" || payload.isOnboarded === true,
        isIdentityFallback: true,
      };

      if (typeof navigator !== "undefined" && navigator.onLine === false) {
        return fallbackIdentity;
      }

      const response = await axiosInstance.get(`${API_URL}/auth/me`);
      const data = response.data;

      if (typeof window !== "undefined" && !(window as any).__collectoHubAuthResolveMs) {
        (window as any).__collectoHubAuthResolveMs = Math.round(performance.now());
      }

      return {
        id: data.id,
        name: `${data.name} ${data.surname}`.trim() || data.userName,
        surname: data.surname || data.Surname,
        firstName: data.name || data.Name,
        userName: data.userName || data.UserName,
        email: data.email || data.Email,
        avatar: getUserAvatarUrl(data.imageUrl || data.ImageUrl),
        imageUrl: data.imageUrl || data.ImageUrl,
        activeFrame: data.activeFrame || data.ActiveFrame,
        activeCardFrame: data.activeCardFrame || data.ActiveCardFrame,
        activeTheme: data.activeTheme || data.ActiveTheme,
        activeBadge: data.activeBadge || data.ActiveBadge,
        activeBadges: data.activeBadges || data.ActiveBadges,
        roles: data.roles || data.Roles,
        isOnboarded: data.isOnboarded ?? data.IsOnboarded,
        hasActiveNoAds: data.hasActiveNoAds ?? data.HasActiveNoAds ?? false,
        bio: data.bio || data.Bio,
        isProfilePublic: data.isProfilePublic ?? data.IsProfilePublic,
        isIdentityFallback: false,
      };
    } catch (error) {
      if (!isTransientNetworkError(error)) {
        console.error("[authProvider.getIdentity] Error:", error);
      }
      const payload = decodeJwt(token);
      if (!payload) return null;
      return {
        id: payload.sub || payload.id || payload["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier"],
        name: payload.username || payload.name || payload.email || payload["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name"],
        userName: payload.userName || payload.username || payload.name || payload["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name"],
        email: payload.email || payload["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress"],
        avatar: getUserAvatarUrl(payload.imageUrl || payload.avatar),
        activeFrame: payload.activeFrame || payload.ActiveFrame,
        activeCardFrame: payload.activeCardFrame || payload.ActiveCardFrame,
        activeTheme: payload.activeTheme || payload.ActiveTheme,
        activeBadge: payload.activeBadge || payload.ActiveBadge,
        activeBadges: payload.activeBadges || payload.ActiveBadges,
        roles: [],
        isOnboarded: payload.isOnboarded === "True" || payload.isOnboarded === true,
        isIdentityFallback: true,
      };
    }
  },

  onError: async (error) => {
    if (error?.statusCode === 401 || error?.response?.status === 401) {
      if (hasNativeAuthBridge()) {
        const renewed = await requestNativeAccessToken(true);
        if (renewed) return { error };
        localStorage.removeItem(TOKEN_KEY);
        window.dispatchEvent(new CustomEvent("auth-state-changed"));
        return { logout: true, error };
      }
        try {
          const response = await axiosInstance.post(`${API_URL}/auth/refresh-token`, null, { withCredentials: true });
          const data = response.data;
          localStorage.setItem(TOKEN_KEY, data.token);
          localStorage.removeItem(APP_CONFIG.STORAGE_KEYS.REFRESH);
          window.dispatchEvent(new CustomEvent("auth-state-changed"));
          return { error }; // Retry will happen with new token
        } catch {
          chLogger("[Auth] onError: 401 & refresh FAILED");
          localStorage.removeItem(TOKEN_KEY);
          localStorage.removeItem(APP_CONFIG.STORAGE_KEYS.REFRESH);
          window.dispatchEvent(new CustomEvent("auth-state-changed"));
          return { logout: true, error };
        }
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(APP_CONFIG.STORAGE_KEYS.REFRESH);
      window.dispatchEvent(new CustomEvent("auth-state-changed"));
      return { logout: true, error };
    }
    return { error };
  },
};
